/**
 * **Last reset** in the drawer's Status list: who pressed Reset database last, and
 * when. The reset writes the demo-day marker as the presser, so the marker's
 * `createdBy` and `createdAt` are the answer (`data/horizonQueries.ts`). A seed
 * applied by hand names whoever it was generated for.
 */
import { ListItem, ListItemText } from "@mui/material";
import { useHomeHealthDemoLastResetQuery } from "~/graphql/__generated__/graphql";
import { Permission, useHasPermission } from "~/hooks/permissions";
import type { BranchClock } from "../components/branchClock";

interface OrgUserLike {
  userId: number;
  user?: { firstName: string; lastName: string; email: string } | null;
}

/** The marker row's id from the store's seed stamp, `"<id>:<day>"`. */
export function markerIdFromStamp(stamp: string | undefined): number | undefined {
  const id = Number(stamp?.split(":")[0]);
  return stamp && Number.isInteger(id) && id > 0 ? id : undefined;
}

/** The presser's name, their email when the name is blank, or their user id when they have left the org. */
export function resetByName(createdBy: number | null | undefined, orgUsers: readonly OrgUserLike[]): string | undefined {
  if (createdBy == null) return undefined;
  const user = orgUsers.find((u) => u.userId === createdBy)?.user;
  if (!user) return `user ${createdBy}`;
  return `${user.firstName} ${user.lastName}`.trim() || user.email;
}

export function LastResetItem({ stamp, clock }: { stamp: string | undefined; clock: BranchClock }) {
  const canRead = useHasPermission(Permission.copyDemoData) === true;
  const markerId = markerIdFromStamp(stamp);
  const { data } = useHomeHealthDemoLastResetQuery({ variables: { markerId: markerId ?? 0 }, skip: !canRead || markerId === undefined });
  const marker = data?.service[0];
  if (!marker) return null;
  const by = resetByName(marker.createdBy, data.organizationUser);
  return (
    <ListItem disableGutters>
      <ListItemText primary="Last reset" secondary={by ? `${clock.dateTime(marker.createdAt)} by ${by}` : clock.dateTime(marker.createdAt)} />
    </ListItem>
  );
}
